<template>
    <div>
       <div class="row row-spacing mb-2">
        <div class="col-md-12">
            <button class="btn btn-info" @click="addPackage()">
                Add Package
            </button>
            <label class="text-warning" v-if="pricedFromQuote">&nbsp; Prices are determined during HALO Quotation.</label>
            <label class="text-warning" v-else>&nbsp; Prices are for HALO Quotation only. Billing might be different</label>
        </div>
       </div>
       <div class="row row-spacing mb-2">
        <div class="col-md-12">
            <div class="table-responsive table-bordered" v-if="quotationPackages.length > 0">
                <table class="table">
                    <thead>
                        <tr class="text-nowrap">
                            <th>Description</th>
                            <th>Contract Term</th>
                            <th>Billing Frequency</th>
                            <th v-if="!pricedFromQuote">Currency</th>
                            <th v-if="!pricedFromQuote">Price</th>
                            <th v-if="!pricedFromQuote">Price (R)</th>
                            <th class="pricing-status-column">Expired</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody 
                        v-for="quotationPackage in quotationPackages"
                        :key="quotationPackage.objKey"
                        :class="{ 'bg-primary': quotationPackage.expired }"
                    >
                        <tr>
                            <td class="pricing-label-column">{{ quotationPackage.label }}</td>
                            <td class="text-nowrap text-center">{{ quotationPackage.contractTerm }}</td>
                            <td class="text-nowrap text-center">{{ quotationPackage.billingFrequency }}</td>
                            <td v-if="!pricedFromQuote" class="text-nowrap text-center">{{ quotationPackage.currency }}</td>
                            <td v-if="!pricedFromQuote" class="text-nowrap">{{ currencySymbol(quotationPackage.currency) + " " + quotationPackage.priceInCurrency }}</td>
                            <td v-if="!pricedFromQuote" class="text-nowrap">{{ decimalFormat(quotationPackage.priceInRands) }}</td>
                            <td class="text-nowrap text-center">
                                <span class="badge" :class="quotationPackage.expired ? 'badge-warning' : 'badge-green'">
                                    {{ quotationPackage.expired }}
                                </span>
                            </td>
                            <td class="text-center">
                                <button
                                    v-if="userEditRight"                  
                                    v-tooltip="'Edit Package'"
                                    @click="editPackage(quotationPackage)"
                                    type="button"
                                    class="btn btn-sm btn-info m-1"
                                >
                                    <em class="fa fa-edit fa-fw"></em>
                                </button>
                                <button
                                    v-if="userEditRight"                  
                                    v-tooltip="'Delete Package'"
                                    type="button"
                                    @click="deletePackage(quotationPackage)"
                                    class="btn btn-sm btn-primary"
                                >
                                    <em class="fa fa-trash fa-fw"></em>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="alert alert-info">No Quotation Packages Available</div>
        </div>
       </div>
       <ModifyQuotationPackageModal 
        ref="modifyQuotationPackageModal"
        :quotationPackage="quotationPackage"
        :contractTerms="contractTerms"
        :billingFrequency="billingFrequency"
        :currencies="currencies"
        :modalTitle="modalTitle"
        :pricedFromQuote="pricedFromQuote"
        @createQuotationPackage="createQuotationPackage"
        @getExchangeRate="getExchangeRate"
        @updateQuotationPackage="updateQuotationPackage"
       />
    </div>
</template>

<script>
import PriceFunctions from "@/components/Admin/PriceFunctions";
import ModifyQuotationPackageModal from "@/components/Admin/Modals/ModifyQuotationPackageModal.vue";

export default {
    extends: PriceFunctions,
    components: {
        ModifyQuotationPackageModal
    },
    props: {
        productNumber: {
            type: Number,
            required: true,
        },
        productVendor: {
            type: String,
            required: true,
        },
        userEditRight: {
            type: Boolean,
            required: true,
        },
        pricedFromQuote: {
            type: Boolean,
            required: true,
        }
    },
    data() {
        return {
            quotationPackages: [],
            quotationPackage: {},
            period: this.getCurrentPeriod(),
            rowPrice: [],
            newPrice: {},
            contractTerms: [],
            billingFrequency: [],
            currencies: [],
            modalTitle: "",
            exchangeRate: {}
        }
    },
    mounted() {
        this.getQuotationPackages();
        this.getContractTerms();
        this.getBillingFrequency();
        this.getCurrencies();
    },
    methods: {
        getQuotationPackages: function() {
            const hostUrl = this.$config.aimsAPI;
            this.$http.get(`${hostUrl}pricing/quotationpackages/${this.productVendor.toLowerCase()}/${this.productNumber}`).then(
                (response) => {
                    this.quotationPackages = response.data;
                },
                (error) => {
                    console.error(error);
                    this.showError("Error fetching Quotation Packages", error);
                }
            );
        },
        getEmptyQuotationPackage: function() {
            const hostUrl = this.$config.aimsAPI;
            this.$http.get(`${hostUrl}pricing/quotationpackages/${this.productVendor.toLowerCase()}/empty`).then(
                (response) => {
                    this.quotationPackage = response.data;     
                },
                (error) => {
                    console.error(error);
                    this.showError("Error fetching empty Quotation Package", error);
                }
            );
        },
        getContractTerms: function() {
            const hostUrl = this.$config.aimsAPI;
            this.$http.get(`${hostUrl}pricing/quotationpackages/terms`).then(
                (response) => {
                    this.contractTerms = response.data;     
                },
                (error) => {
                    console.error(error);
                    this.showError("Error fetching contract terms", error);
                }
            );
        },
        getBillingFrequency: function() {
            const hostUrl = this.$config.aimsAPI;
            this.$http.get(`${hostUrl}pricing/quotationpackages/billingfrequency`).then(
                (response) => {
                    this.billingFrequency = response.data;                
                },
                (error) => {
                    console.error(error);
                    this.showError("Error fetching Billing Frequency", error);
                }
            );
        },
        getCurrencies: function() {
            const hostUrl = this.$config.aimsAPI;
            this.$http.get(`${hostUrl}pricing/quotationpackages/currencies`).then(
                (response) => {
                    this.currencies = response.data;
                },
                (error) => {
                    console.error(error);
                    this.showError("Error fetching currencies", error);
                }
            );
        },
        addPackage: function() {
            this.modalTitle = "Add Quotation Package";
            this.getEmptyQuotationPackage();
            this.$refs.modifyQuotationPackageModal.open();
        },
        getExchangeRate: function(currency) {
            if (currency !== 'ZAR') {
                const hostUrl = this.$config.aimsAPI;
                this.$http.get(`${hostUrl}pricing/quotationpackages/exchangerate/${currency}`).then(
                    (response) => {
                        this.exchangeRate = response.data;     
                        this.quotationPackage.exchangeRate = this.exchangeRate.exchangeRateZar;
                    },
                    (error) => {
                        console.error(error);
                        this.showError("Error fetching exchange rate", error);
                    }
                );
            } 
        },
        createQuotationPackage: function(pkg) {
            pkg.productNumber = this.productNumber;
            const hostUrl = this.$config.aimsAPI;
            this.$http.post(`${hostUrl}pricing/quotationpackages/${this.productVendor.toLowerCase()}`, pkg).then(
                (response) => {
                    this.showSuccess("Package Created");  
                    this.getQuotationPackages();
                },
                (error) => {
                    console.error(error);
                    this.showError("Error creating Quotation Package", error);
                }
            );
        },
        editPackage: function(pkg) {
            this.modalTitle = "Update Quotation Package";
            this.quotationPackage = this.deepCopyObject(pkg);
            this.$refs.modifyQuotationPackageModal.open();
        },
        updateQuotationPackage: function(pkg) {
            const hostUrl = this.$config.aimsAPI;
            this.$http.put(`${hostUrl}pricing/quotationpackages/${this.productVendor.toLowerCase()}`, pkg).then(
                (response) => {
                    this.showSuccess("Package Saved");  
                    this.getQuotationPackages();
                },
                (error) => {
                    console.error(error);
                    this.showError("Error updating Quotation Package", error);
                }
            );
        },
        deletePackage: function(pkg) {
            this.$swal({
                title: "Are you sure you want to delete this Package?",
                type: "warning",
                showCancelButton: true,
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes",
            }).then((result) => {
                if (result.value) {
                    const hostUrl = this.$config.aimsAPI;
                    this.$http.delete(`${hostUrl}pricing/quotationpackages/${this.productVendor.toLowerCase()}/${pkg.objKey}`).then(
                        (response) => {    
                            this.showSuccess("Quotation package deleted");
                            this.getQuotationPackages();
                        },
                        (error) => {
                            this.showError("Error deleting Quotation Package", error);
                        }
                    );
                }
            });
        },
        currencySymbol(currency) {
            if (currency == "USD") {
                return "$"
            } else if (currency == "EUR") {
                return "€"
            } else if (currency == "GBP") {
                return "£"
            } else {
                return "R"
            }
        },
        decimalFormat(value) {
            if (value > 0) {
            return "R " + parseFloat(value).toFixed(2);
            } else {
            return "-";
            }
        },
    }
}
</script>

<style scoped>
.filter-container {
  position: relative;
  z-index: 999;
}

.filter-container .filter-wrapper {
  position: absolute;
  z-index: 999;
  margin-top: .5em;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  padding: 2px;
  width: 350px;
  right: 90;
  top: -42;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

.filter-container .filter-wrapper .filter-body {
  max-height: 200px;
  overflow: scroll;
  overflow-x: hidden;
  padding: 10px;
  text-align: left;
}

th.table-font-size {
  font-size: .95rem;
}
th.table-header-width-1 {
  width: 10px;
}
th.table-header-width-2 {
  width: 20px;
}
th.table-header-width-3 {
  width: 50px;
}
th.table-header-width-4 {
  width: 60px;
}
th.table-header-width-5 {
  width: 95px;
}
th.table-header-width-6 {
  width: 200px;
}

.font-size {
  font-size: .9rem;
}
</style>